// apps/b2b/src/lib/api.ts
// B2B-специфичные эндпоинты. Общее ядро — в @tb/ui/api-core
import {
  CompaniesService,
  AssessmentService,
  AnalyticsService,
  HiringService,
  InvitationsService,
  MatchService,
  TestsService,
} from '@tb/api-client/client';
import { withToken, fetchWithToken, auth, extractToken, setToken, getToken } from '@tb/ui/api-core';

export const api = {
  // ===== AUTH (из общего ядра) =====
  ...auth,

  // ===== COMPANIES =====
  myCompanies: () => withToken(CompaniesService.companiesControllerMy)(),
  companyOne: (id: string) => withToken(CompaniesService.companiesControllerOne)({ id }),
  createCompany: (body: any) => withToken(CompaniesService.companiesControllerCreate)({ requestBody: body }),
  listMembers: (companyId: string) =>
    withToken(CompaniesService.companiesControllerMembers)({ id: companyId }),
  blockMember: (companyId: string, userId: string) =>
    withToken(CompaniesService.companiesControllerBlock)({ id: companyId, userId }),
  confirmMember: (companyId: string, userId: string) =>
    withToken(CompaniesService.companiesControllerApprove)({ id: companyId, userId }),
  updateJoinSettings: (companyId: string, body: { joinPassword?: string | null }) =>
    withToken(CompaniesService.companiesControllerUpdateJoinSettings)({ id: companyId, requestBody: body }),
  changeMemberRole: (companyId: string, userId: string, role: string) =>
    withToken(CompaniesService.companiesControllerChangeRole)({ id: companyId, userId, requestBody: { role } }),
  dashboard: (companyId: string) =>
    withToken(CompaniesService.companiesControllerDashboard)({ id: companyId }),

  // ===== TEAMS =====
  listTeams: (companyId: string) =>
    withToken(CompaniesService.companiesControllerTeams)({ id: companyId }),
  createTeam: (companyId: string, body: any) =>
    withToken(CompaniesService.companiesControllerCreateTeam)({ id: companyId, requestBody: body }),
  teamOne: (companyId: string, teamId: string) =>
    withToken(CompaniesService.companiesControllerTeam)({ id: companyId, teamId }),
  addTeamMember: (companyId: string, teamId: string, userId: string) =>
    withToken(CompaniesService.companiesControllerAddTeamMember)({ id: companyId, teamId, requestBody: { userId } }),
  setTeamLeader: (companyId: string, teamId: string, leaderId: string) =>
    withToken(CompaniesService.companiesControllerSetLeader)({ id: companyId, teamId, requestBody: { leaderId } }),
  listTeamMembers: (companyId: string, teamId: string) =>
    withToken(CompaniesService.companiesControllerTeamMembers)({ id: companyId, teamId }),
  removeTeamMember: (companyId: string, teamId: string, userId: string) =>
    withToken(CompaniesService.companiesControllerRemoveTeamMember)({ id: companyId, teamId, userId }),
  deleteTeam: (companyId: string, teamId: string) =>
    fetchWithToken(`/api/companies/${companyId}/teams/${teamId}`, { method: 'DELETE' }),

  // ===== ANALYTICS =====
  teamBundle: (companyId: string, teamId: string) =>
    withToken(AnalyticsService.analyticsControllerTeamBundle)({ id: companyId, tid: teamId }),
  companyInsights: (companyId: string) =>
    withToken(AnalyticsService.analyticsControllerCompanyInsights)({ id: companyId }),
  memberDashboard: (companyId: string, teamId: string, userId: string) =>
    fetchWithToken(`/api/analytics/companies/${companyId}/teams/${teamId}/members/${userId}/dashboard`),
  refreshTeamProfile: (companyId: string, teamId: string) =>
    withToken(MatchService.matchControllerRefresh)({ id: companyId, tid: teamId }),

  // ===== TEAM TESTS =====
  listTeamTests: (companyId: string, teamId: string) =>
    withToken(MatchService.matchControllerListTeamTests)({ id: companyId, tid: teamId }),
  addTeamTest: (companyId: string, teamId: string, testId: string) =>
    withToken(MatchService.matchControllerAddTeamTest)({ id: companyId, tid: teamId, requestBody: { testId } }),
  removeTeamTest: (companyId: string, teamId: string, testId: string) =>
    withToken(MatchService.matchControllerRemoveTeamTest)({ id: companyId, tid: teamId, testId }),

  // ===== HIRING (ВАКАНСИИ) =====
  listHiring: (companyId: string) => withToken(HiringService.hiringControllerList)({ id: companyId }),
  createHiring: (companyId: string, body: any) =>
    withToken(HiringService.hiringControllerCreate)({ id: companyId, requestBody: body }),
  hiringOne: (companyId: string, hid: string) =>
    withToken(HiringService.hiringControllerOne)({ id: companyId, hid }),
  updateHiring: (companyId: string, hid: string, body: any) =>
    withToken(HiringService.hiringControllerUpdate)({ id: companyId, hid, requestBody: body }),
  hiringUseTeamAverage: (companyId: string, hid: string) =>
    withToken(HiringService.hiringControllerUseTeamAverage)({ id: companyId, hid }),
  hiringUsePerformance: (companyId: string, hid: string) =>
    withToken(HiringService.hiringControllerUsePerformance)({ id: companyId, hid }),
  hiringClone: (companyId: string, hid: string) =>
    withToken(HiringService.hiringControllerClone)({ id: companyId, hid }),
  hiringPublish: (companyId: string, hid: string) =>
    withToken(HiringService.hiringControllerPublish)({ id: companyId, hid }),
  hiringRenew: (companyId: string, hid: string, days: number = 60) =>
    withToken(HiringService.hiringControllerRenew)({ id: companyId, hid, days: String(days) }),
  hiringComputeMatch: (companyId: string, hid: string) =>
    withToken(HiringService.hiringControllerCompute)({ id: companyId, hid, candidateId: '' }),
  setCandidateStatus: (
    companyId: string,
    hid: string,
    cid: string,
    body: { reviewStatus?: 'pending' | 'accepted' | 'rejected'; reviewComment?: string; status?: string },
  ) => withToken(HiringService.hiringControllerSetCandidateStatus)({ id: companyId, hid, cid, requestBody: body }),
  moveCandidateToTeam: (companyId: string, hid: string, cid: string, role?: string) =>
    withToken(HiringService.hiringControllerMoveToTeam)({ id: companyId, hid, cid, requestBody: role ? { role } : {} }),
  removeCandidate: (companyId: string, hid: string, cid: string) =>
    withToken(HiringService.hiringControllerRemoveCandidate)({ id: companyId, hid, cid }),

  // ===== HIRING TESTS =====
  listHiringTests: (companyId: string, hid: string) =>
    withToken(HiringService.hiringControllerListTests)({ id: companyId, hid }),
  addHiringTest: (companyId: string, hid: string, testId: string) =>
    withToken(HiringService.hiringControllerAddTest)({ id: companyId, hid, requestBody: { testId } }),
  removeHiringTest: (companyId: string, hid: string, testId: string) =>
    withToken(HiringService.hiringControllerRemoveTest)({ id: companyId, hid, testId }),

  // ===== INVITES (ПРИГЛАШЕНИЯ) =====
  createInvite: (
    companyId: string,
    hid: string,
    body?: {
      inviteType?: 'single' | 'multi';
      maxUses?: number | null;
      accessCode?: string | null;
      email?: string;
    },
  ) => withToken(InvitationsService.invitationsControllerInvite)({ companyId, hid, requestBody: body ?? {} }),
  listInvites: (companyId: string, hid: string) =>
    withToken(InvitationsService.invitationsControllerList)({ companyId, hid }),

  // ===== TESTS (КАТАЛОГ) =====
  listTests: () => withToken(TestsService.testsControllerList)({ b2BOnly: true }),
  testOne: (id: string) => withToken(TestsService.testsControllerOne)({ id }),

  // ===== ASSESSMENT =====
  getDescription: (test: string, aspect: string, gradation: string) =>
    withToken(AssessmentService.assessmentControllerGetDescription)({
      test,
      aspect,
      gradation,
      value: gradation,
    }),
  bigfiveTypes: () => withToken(AssessmentService.assessmentControllerBigFiveTypes)(),
  bigfiveRoles: () => withToken(AssessmentService.assessmentControllerBigFiveRoles)(),
  bigfiveByCode: (code: string) =>
    withToken(AssessmentService.assessmentControllerBigFiveByCode)({ code }),
};

export { extractToken, setToken, getToken };
